import type {
  ApiErrorBody,
  ApprovalDetail,
  BillingEmbedToken,
  BillingHealth,
  BootstrapPayload,
  CatalogAgent,
  CatalogResponse,
  ChatSessionDetail,
  ChatSessionListResponse,
  ChatSessionSummary,
  ChatStreamEvent,
  DecisionReasonCode,
  ExplicacionResponse,
  GA4PropertiesResponse,
  GA4PropertySelectResponse,
  InlineCitationDetail,
  InsightListChecksResponse,
  InstallationConfigResponse,
  InstallationDetail,
  MetaAdsAccountsResponse,
  MetaAdsAccountSelectResponse,
  OAuthStartResponse,
  RecommendationCounts,
  RecommendationListResponse,
  RunSummary,
} from "./types";
import {
  getSessionToken,
  getStoreInfo,
  getUserAdminInfo,
} from "@tiendanube/nexo";

import nexo from "./NexoClient";
import { useSessionStore } from "./session";

// Local Store/User shapes the backend's /api/v1/auth/nexo-session expects.
// Nexo's getStoreInfo / getUserAdminInfo return objects that structurally
// satisfy these; not imported from @tiendanube/nexo because exported types
// vary across SDK versions.
export interface StoreInfo {
  id: string;
  name: string;
  url?: string;
  country?: string;
  language?: string;
  currency?: string;
}

export interface UserInfo {
  id: string;
  name?: string;
  roles?: string[];
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

// ---------------------------------------------------------------------------
// JWT refresh — Axios-style interceptor pattern using fetch.
//
// Backend issues short-lived app JWTs (~30 min). When a query returns 401, we
// silently re-handshake with Nexo (which mints a fresh session token from
// the parent admin) and re-exchange it for a new app JWT. This mirrors the
// Storydots template's Axios interceptor and prevents the dashboard from
// breaking after the JWT expires.
// ---------------------------------------------------------------------------

let refreshInFlight: Promise<string> | null = null;

async function refreshAppJwt(): Promise<string> {
  // De-dupe concurrent refreshes — multiple in-flight queries hitting 401
  // simultaneously must NOT each spawn their own handshake.
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const sessionToken = await getSessionToken(nexo);
      if (!sessionToken) {
        throw new Error("Nexo no devolvió un session token");
      }
      const [storeInfo, userInfo] = await Promise.all([
        getStoreInfo(nexo).catch(() => undefined),
        getUserAdminInfo(nexo).catch(() => undefined),
      ]);
      const fresh = await exchangeNexoSessionToken(
        sessionToken,
        storeInfo,
        userInfo,
      );
      useSessionStore.getState().setAccessToken(fresh);
      return fresh;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface AuthedFetchOptions extends RequestInit {
  // Set to false to skip the auto-401 → refresh → retry loop (e.g., for the
  // initial token-exchange call which has no JWT yet).
  refreshOn401?: boolean;
}

async function authedFetch(
  path: string,
  options: AuthedFetchOptions = {},
): Promise<Response> {
  const { refreshOn401 = true, headers: rawHeaders, ...rest } = options;
  const accessToken = useSessionStore.getState().accessToken;

  const headers = new Headers(rawHeaders);
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, { ...rest, headers });

  if (response.status !== 401 || !refreshOn401 || !accessToken) {
    return response;
  }

  // 401 with an existing token → expired; refresh once and retry.
  let freshToken: string;
  try {
    freshToken = await refreshAppJwt();
  } catch {
    // Refresh itself failed — return original 401 so the caller can show the
    // "no pudimos iniciar sesión" state and the user can reload from TN admin.
    return response;
  }
  const retryHeaders = new Headers(rawHeaders);
  retryHeaders.set("Authorization", `Bearer ${freshToken}`);
  return fetch(`${API_BASE_URL}${path}`, { ...rest, headers: retryHeaders });
}

async function parseError(
  response: Response,
  fallback: string,
): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    body = null;
  }
  const detail = body?.detail || fallback;
  return new ApiError(
    detail,
    body?.error?.code ?? "",
    body ?? null,
    response.status,
  );
}

export class ApiError extends Error {
  code: string;
  body: ApiErrorBody | null;
  status: number;

  constructor(
    message: string,
    code: string,
    body: ApiErrorBody | null,
    status: number,
  ) {
    super(message);
    this.code = code;
    this.body = body;
    this.status = status;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function exchangeNexoSessionToken(
  nexoSessionToken: string,
  storeInfo?: StoreInfo,
  userInfo?: UserInfo,
): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/nexo-session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${nexoSessionToken}`,
    },
    body: JSON.stringify({
      store_info: storeInfo,
      user_info: userInfo,
    }),
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      detail?: string;
    } | null;
    throw new Error(
      payload?.detail ?? "No pudimos iniciar sesión desde TiendaNube.",
    );
  }

  const payload = (await response.json()) as { access_jwt: string };
  return payload.access_jwt;
}

export async function fetchBootstrap(
  accessToken: string,
): Promise<BootstrapPayload> {
  // Pass `accessToken` through useSessionStore so authedFetch picks it up.
  // The argument is kept for backward-compat with Dashboard's call site.
  if (accessToken && useSessionStore.getState().accessToken !== accessToken) {
    useSessionStore.getState().setAccessToken(accessToken);
  }
  const response = await authedFetch("/api/v1/bootstrap");

  if (!response.ok) {
    throw await parseError(
      response,
      "No pudimos cargar el tablero de agentes.",
    );
  }

  return (await response.json()) as BootstrapPayload;
}

// Either `{ to_state, ... }` for a state transition OR `{ archive: true }`
// for a merchant-initiated archive of a terminal rec. The backend enforces
// mutual exclusion + that archive requires the rec be terminal already.
// We keep the type loose (all optional) because TS's discriminated-union
// narrowing through `useMutation`'s generic surface produced excess-property
// errors at call sites; semantic enforcement lives at the API boundary.
export type PatchRecommendationBody = {
  to_state?: "todo" | "in_progress" | "done" | "wont_do";
  wont_do_reason_es?: string;
  wont_do_reason_code?: string;
  archive?: boolean;
};

export async function patchRecommendation(
  recommendationId: string,
  body: PatchRecommendationBody,
): Promise<import("./types").PlanTodayRow> {
  const response = await authedFetch(
    `/api/v1/recommendations/${encodeURIComponent(recommendationId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos actualizar la recomendación.");
  }
  return (await response.json()) as import("./types").PlanTodayRow;
}

// L97 — `plans/17` §7.1 paginated recommendation list used by the Acciones tab
// board / focus strip / list view.
export async function fetchRecommendations(params?: {
  state?: "open" | "todo" | "in_progress" | "done" | "wont_do" | "all";
  installation_id?: string;
  limit?: number;
  offset?: number;
}): Promise<RecommendationListResponse> {
  const qs = new URLSearchParams();
  if (params?.state) qs.set("state", params.state);
  if (params?.installation_id) qs.set("installation_id", params.installation_id);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const url = `/api/v1/recommendations${qs.toString() ? `?${qs}` : ""}`;
  const response = await authedFetch(url);
  if (!response.ok) {
    throw await parseError(response, "No pudimos cargar las recomendaciones.");
  }
  return (await response.json()) as RecommendationListResponse;
}

// L97 — `plans/17` §7.5 counts used by the kanban header strip.
// L98 — `installation_id` param added so AgentInsights Surface B can scope
// `by_state` counts to one agent; `by_agent` is still store-wide.
export async function fetchRecommendationCounts(params?: {
  installation_id?: string;
}): Promise<RecommendationCounts> {
  const qs = new URLSearchParams();
  if (params?.installation_id) qs.set("installation_id", params.installation_id);
  const url = `/api/v1/recommendations/counts${qs.toString() ? `?${qs}` : ""}`;
  const response = await authedFetch(url);
  if (!response.ok) {
    throw await parseError(response, "No pudimos cargar los conteos de recomendaciones.");
  }
  return (await response.json()) as RecommendationCounts;
}

export async function fetchAgentCatalog(): Promise<CatalogResponse> {
  const response = await authedFetch("/api/v1/agents/catalog");
  if (!response.ok) {
    throw await parseError(response, "No pudimos leer el catálogo de agentes.");
  }
  return (await response.json()) as CatalogResponse;
}

export async function fetchAgentCatalogDetail(
  slug: string,
): Promise<CatalogAgent> {
  const response = await authedFetch(
    `/api/v1/agents/catalog/${encodeURIComponent(slug)}`,
  );
  if (!response.ok) {
    throw await parseError(
      response,
      "No encontramos ese agente en el catálogo.",
    );
  }
  return (await response.json()) as CatalogAgent;
}

export async function hireAgent(
  slug: string,
  opts?: { skipConnectors?: boolean },
): Promise<InstallationDetail> {
  // `skip_connectors` (2026-06-22): re-entry from /access where the merchant
  // explicitly chose to activate the agent without authorizing every recommended
  // fuente. The backend clears the awaiting pause and resumes scheduling so the
  // agent runs with partial data. Ignored on a brand-new hire (no install yet).
  const response = await authedFetch("/api/v1/agents/installations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(
      opts?.skipConnectors ? { slug, skip_connectors: true } : { slug },
    ),
  });
  if (!response.ok) {
    throw await parseError(response, "No pudimos contratar al agente.");
  }
  return (await response.json()) as InstallationDetail;
}

// Sample run (2026-06-18). `POST /api/v1/agents/installations/sample` with
// `{slug}` launches the merchant's ONE free sample run for this agent — a real
// pipeline run in the non-billable PREVIEW lifecycle state that produces real
// insights/actions/recs but reports NO billing usage. The backend is idempotent:
// re-posting after a sample already exists returns the same preview install
// (200) instead of queueing a second sample. There is NO billing gate on the
// sample — it is free and a conversion tool. Error handling mirrors `hireAgent`.
export async function startSampleRun(
  slug: string,
): Promise<InstallationDetail> {
  const response = await authedFetch("/api/v1/agents/installations/sample", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug }),
  });
  if (!response.ok) {
    throw await parseError(response, "No pudimos iniciar la corrida de muestra.");
  }
  return (await response.json()) as InstallationDetail;
}

export async function fetchInstallation(
  installationId: string,
): Promise<InstallationDetail> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}`,
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos leer el estado del agente.");
  }
  return (await response.json()) as InstallationDetail;
}

// P2-5 / Leo-2 FL7 — persist the catalog hire-survey answers onto
// `installation.config` (the Wave-3 backend accessors + AC-05 gate read these).
// Each field is optional: an absent key leaves the prior answer untouched, so
// a partial submit never clears a previous response. `seller_type: null`
// explicitly clears the axis ("prefiero no decir").
export async function saveHireSurvey(
  installationId: string,
  answers: {
    manages_skus?: boolean;
    has_sizes?: boolean;
    seller_type?: "fabricante" | "revendedor" | "importador" | null;
  },
): Promise<InstallationDetail> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/hire-survey`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(answers),
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos guardar tus respuestas.");
  }
  return (await response.json()) as InstallationDetail;
}

export async function fetchAgentRuns(
  installationId: string,
): Promise<import("./types").AgentRunListResponse> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/runs`,
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos leer el historial de corridas.");
  }
  return (await response.json()) as import("./types").AgentRunListResponse;
}

// L117 — `plans/10` §12 + `plans/11` §4.7. Per-installation audit projection
// powering AgentProfile's "Eventos recientes" strip. Cached briefly because
// the strip is a glance surface — the merchant doesn't expect live tailing.
export async function fetchInstallationAuditEvents(
  installationId: string,
  options?: { limit?: number },
): Promise<import("./types").MerchantAuditEventListResponse> {
  const qs = new URLSearchParams();
  if (options?.limit != null) qs.set("limit", String(options.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/audit-events${suffix}`,
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos leer los movimientos del agente.");
  }
  return (await response.json()) as import("./types").MerchantAuditEventListResponse;
}

export async function triggerFirstRun(
  installationId: string,
): Promise<RunSummary> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/runs`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ trigger: "first_run" }),
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos iniciar la primera corrida.");
  }
  return (await response.json()) as RunSummary;
}

export async function triggerAnalysisRun(
  installationId: string,
): Promise<RunSummary> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/runs`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ trigger: "merchant_demand" }),
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos iniciar el análisis.");
  }
  return (await response.json()) as RunSummary;
}

export async function startConnectorOAuth(
  kind: string,
): Promise<OAuthStartResponse> {
  const response = await authedFetch(
    `/api/v1/auth/${encodeURIComponent(kind)}/start`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos iniciar la autorización.");
  }
  return (await response.json()) as OAuthStartResponse;
}

export async function disconnectConnector(kind: string): Promise<void> {
  const response = await authedFetch(
    `/api/v1/connectors/${encodeURIComponent(kind)}/disconnect`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos desconectar la fuente.");
  }
}

export async function fetchMetaAdsAccounts(): Promise<MetaAdsAccountsResponse> {
  const response = await authedFetch("/api/v1/connectors/meta_ads/accounts");
  if (!response.ok) {
    throw await parseError(
      response,
      "No pudimos leer tus cuentas de Meta Ads.",
    );
  }
  return (await response.json()) as MetaAdsAccountsResponse;
}

export async function selectMetaAdsAccount(
  accountId: string,
): Promise<MetaAdsAccountSelectResponse> {
  const response = await authedFetch(
    "/api/v1/connectors/meta_ads/accounts/select",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ account_id: accountId }),
    },
  );
  if (!response.ok) {
    throw await parseError(
      response,
      "No pudimos elegir esa cuenta de Meta Ads.",
    );
  }
  return (await response.json()) as MetaAdsAccountSelectResponse;
}

export async function fetchGA4Properties(): Promise<GA4PropertiesResponse> {
  const response = await authedFetch(
    "/api/v1/connectors/google_analytics_4/properties",
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos leer tus propiedades de GA4.");
  }
  return (await response.json()) as GA4PropertiesResponse;
}

export async function selectGA4Property(
  propertyId: string,
): Promise<GA4PropertySelectResponse> {
  const response = await authedFetch(
    "/api/v1/connectors/google_analytics_4/properties/select",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ property_id: propertyId }),
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos guardar la propiedad de GA4.");
  }
  return (await response.json()) as GA4PropertySelectResponse;
}

export async function fetchApprovalDetail(
  approvalId: string,
): Promise<ApprovalDetail> {
  const response = await authedFetch(
    `/api/v1/approvals/${encodeURIComponent(approvalId)}`,
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos leer la aprobación.");
  }
  return (await response.json()) as ApprovalDetail;
}

// De-duplication history (2026-05-29). `GET /api/v1/insights/<id>/history`
// returns the older, superseded versions of a canonical observation so the
// merchant can review what an insight replaced. Auth mirrors every other
// embedded call via `authedFetch` (Bearer app-JWT + 401→refresh→retry).
export async function fetchInsightHistory(
  accessToken: string,
  observationId: string,
): Promise<import("./types").InsightHistory> {
  // Keep the session store in sync with the caller-supplied token, mirroring
  // `fetchBootstrap` so call sites can pass the token explicitly while
  // `authedFetch` still reads it from the store.
  if (accessToken && useSessionStore.getState().accessToken !== accessToken) {
    useSessionStore.getState().setAccessToken(accessToken);
  }
  const response = await authedFetch(
    `/api/v1/insights/${encodeURIComponent(observationId)}/history`,
  );
  if (!response.ok) {
    throw await parseError(
      response,
      "No pudimos cargar las versiones anteriores.",
    );
  }
  return (await response.json()) as import("./types").InsightHistory;
}

export async function fetchChatSessions(
  installationId: string,
): Promise<ChatSessionListResponse> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/chat/sessions`,
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos cargar las conversaciones.");
  }
  return (await response.json()) as ChatSessionListResponse;
}

/**
 * fetchChatInbox — merged-thread inbox digest (revamp 2026-05-12).
 *
 * Replaces the N+1 useQueries pattern in `<MergedChatThread>`: one HTTP call
 * returns every session for the installation WITH the last `perSessionLimit`
 * messages each, plus the agent's `suggested_prompts_es`. The frontend builds
 * the merged timeline from a single payload.
 */
export async function fetchChatInbox(
  installationId: string,
  perSessionLimit = 50,
): Promise<import("./types").ChatInboxResponse> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/chat/inbox?per_session_limit=${perSessionLimit}`,
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos cargar el chat.");
  }
  return (await response.json()) as import("./types").ChatInboxResponse;
}

export async function startChatSession(
  installationId: string,
): Promise<ChatSessionDetail> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/chat/sessions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos iniciar la conversación.");
  }
  return (await response.json()) as ChatSessionDetail;
}

// L66 Tomás — SSE step-event stream for chat (`plans/09` §16.1, `plans/11` §9.1.3).
// Yields `ChatStreamEvent` objects as they arrive. The merchant's typing
// indicator switches to step-driven copy ("Pensando…" / "Verificando…" /
// "Proponiendo acción…") as `step_started` events fire. EventSource isn't
// used because the underlying request is a POST with side effects.

async function _ssePost(
  path: string,
  bodyPayload: string,
  token: string | null,
  signal?: AbortSignal,
): Promise<Response> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers,
    body: bodyPayload,
    signal,
  });
}

export async function* streamChatMessage(
  sessionId: string,
  body: string,
  signal?: AbortSignal,
): AsyncGenerator<ChatStreamEvent, void, unknown> {
  // Manually build the fetch because authedFetch awaits-and-discards the
  // response body. We need the Response alive to read the SSE stream.
  // Add the same 401→refresh→retry logic as authedFetch so an expired JWT
  // doesn't silently break mid-session.
  //
  // `signal` (revamp 2026-05-11): the caller may pass an AbortSignal to
  // cancel the stream — typically on component unmount or when the merchant
  // switches to a different agent. We thread it into the underlying fetch
  // AND check it inside the read loop so we abandon cleanly without leaking
  // event yields to a now-stale consumer.
  const path = `/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages/stream`;
  const payload = JSON.stringify({ body });
  let token = useSessionStore.getState().accessToken;
  let response = await _ssePost(path, payload, token, signal);

  if (response.status === 401 && token) {
    try {
      token = await refreshAppJwt();
      response = await _ssePost(path, payload, token, signal);
    } catch {
      // Refresh failed — fall through and let the !response.ok check throw.
    }
  }

  if (!response.ok || !response.body) {
    throw await parseError(response, "No pudimos enviar tu mensaje.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      if (signal?.aborted) {
        await reader.cancel().catch(() => {});
        return;
      }
      const { value, done } = await reader.read();
      if (done) {
        // Flush any trailing event still in the buffer (some SSE streams omit
        // the trailing blank line on close — the parser tolerates this).
        for (const event of consumeSseFrames(buffer + "\n\n")) {
          if (signal?.aborted) return;
          yield event;
        }
        return;
      }
      buffer += decoder.decode(value, { stream: true });
      let frameBoundary = buffer.indexOf("\n\n");
      while (frameBoundary !== -1) {
        const frame = buffer.slice(0, frameBoundary);
        buffer = buffer.slice(frameBoundary + 2);
        const event = parseSseFrame(frame);
        if (event) {
          if (signal?.aborted) return;
          yield event;
        }
        frameBoundary = buffer.indexOf("\n\n");
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// Internal SSE-frame parser used by streamChatMessage.
function parseSseFrame(frame: string): ChatStreamEvent | null {
  let event: string | null = null;
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event: ")) {
      event = line.slice("event: ".length).trim();
    } else if (line.startsWith("data: ")) {
      dataLines.push(line.slice("data: ".length));
    }
  }
  if (!event || dataLines.length === 0) return null;
  try {
    const data = JSON.parse(dataLines.join("\n"));
    return { event, data } as ChatStreamEvent;
  } catch {
    return null;
  }
}

function* consumeSseFrames(buffer: string): Generator<ChatStreamEvent, void, unknown> {
  let frameBoundary = buffer.indexOf("\n\n");
  while (frameBoundary !== -1) {
    const frame = buffer.slice(0, frameBoundary);
    const event = parseSseFrame(frame);
    if (event) yield event;
    buffer = buffer.slice(frameBoundary + 2);
    frameBoundary = buffer.indexOf("\n\n");
  }
}

// Re-export so consumers can use ChatSessionSummary without importing types directly.
export type { ChatSessionSummary };

export async function decideApproval(
  approvalId: string,
  decision: "approve" | "reject",
  reasonEs = "",
  options: {
    // L73 P3 — Camila `16f` §6 row equivalence + Diego `plans/16` §3.10
    // row 5: chat-side approval emits the same `approval.{decision}` event
    // with `surface: "chat"` so audit equivalence holds.
    surface?: "dashboard" | "chat";
    // L73 P3 — the rejection reason-code (additive enum) populated when the
    // merchant denies a proposal via the rationale-chip menu. The backend
    // decision endpoint reads it under the request key `reason_code_es`
    // (views.py ApproveRejectApprovalView) and persists it onto
    // `ApprovalRequest.decision_reason_code_es`; the two names must NOT be
    // conflated — sending `decision_reason_code_es` here silently dropped the
    // merchant's selected reason.
    decisionReasonCode?: string;
  } = {},
): Promise<ApprovalDetail> {
  const body: Record<string, string> = { reason_es: reasonEs };
  if (options.surface) body.surface = options.surface;
  if (options.decisionReasonCode) {
    body.reason_code_es = options.decisionReasonCode;
  }
  const response = await authedFetch(
    `/api/v1/approvals/${encodeURIComponent(approvalId)}/${decision}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos registrar la decisión.");
  }
  return (await response.json()) as ApprovalDetail;
}

// ---------------------------------------------------------------------------
// L73 P3 chat-revamp endpoints (`plans/16` §3.10 + Camila `16f` §3, §4).
// ---------------------------------------------------------------------------

// `GET /api/v1/agents/installations/:id/snapshots/:snapshot_id` — inline
// citation hover-popover lookup. 404 on scope mismatch is the only signal
// surfaced to the merchant (Camila `16f` §3.4 — never echo backend error).
export async function fetchInlineCitation(
  installationId: string,
  snapshotId: string,
): Promise<InlineCitationDetail> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/snapshots/${encodeURIComponent(snapshotId)}`,
  );
  if (!response.ok) {
    // 404 is the canonical scope-mismatch signal; surface a graceful
    // Spanish message rather than echoing backend body (Camila §3.4).
    throw await parseError(response, "Esta cita ya no está disponible.");
  }
  return (await response.json()) as InlineCitationDetail;
}

// `GET /api/v1/agents/installations/:id/explicacion/:run_id` — Explicación
// panel. The redaction contract (Camila `16f` §4) lives server-side; the
// frontend never receives system_prompt or chain-of-thought.
export async function fetchExplicacion(
  installationId: string,
  runId: string,
): Promise<ExplicacionResponse> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/explicacion/${encodeURIComponent(runId)}`,
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos armar la explicación.");
  }
  return (await response.json()) as ExplicacionResponse;
}

// `GET /api/v1/decision-reason-codes` — rationale-chip catalog for the
// chat-side rejection flow (Tomás `16c` §7.3 + Diego `plans/16` §3.10).
export async function fetchDecisionReasonCodes(): Promise<DecisionReasonCode[]> {
  const response = await authedFetch(`/api/v1/decision-reason-codes`);
  if (!response.ok) {
    throw await parseError(
      response,
      "No pudimos cargar las opciones de rechazo.",
    );
  }
  const data = (await response.json()) as
    | DecisionReasonCode[]
    | { codes?: DecisionReasonCode[] };
  return Array.isArray(data) ? data : data.codes ?? [];
}

// L61 Renata — merchant-initiated undo (`plans/10` §6.3).
export async function undoExecutedAction(actionId: string): Promise<ApprovalDetail> {
  const response = await authedFetch(
    `/api/v1/agent-actions/${encodeURIComponent(actionId)}/undo`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos deshacer la acción.");
  }
  return (await response.json()) as ApprovalDetail;
}

// L58 — installation lifecycle mutations (`plans/11` §9.2.4-5).

export async function pauseInstallation(
  installationId: string,
  reasonEs = "",
): Promise<InstallationDetail> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/pause`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason_es: reasonEs }),
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos pausar el agente.");
  }
  return (await response.json()) as InstallationDetail;
}

export async function reactivateInstallation(
  installationId: string,
): Promise<InstallationDetail> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/reactivate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos reactivar el agente.");
  }
  return (await response.json()) as InstallationDetail;
}

export type ResetAgentUsageResult = {
  reset: boolean;
  rows_deleted: Record<string, number>;
};

/**
 * TEMPORARY "zona de prueba" reset — wipe THIS store's hired agents + all their
 * work (insights / actions / runs / chat / worksheet marks) so the merchant can
 * test from a clean slate. Connected data sources are NOT touched. Store-bound
 * (any authenticated session for the store may reset) + irreversible.
 */
export async function resetAgentUsage(): Promise<ResetAgentUsageResult> {
  const response = await authedFetch("/api/v1/agents/reset-usage", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
  if (!response.ok) {
    throw await parseError(response, "No pudimos reiniciar los datos de los agentes.");
  }
  return (await response.json()) as ResetAgentUsageResult;
}

export async function uninstallInstallation(
  installationId: string,
  confirmName: string,
  reasonEs = "",
): Promise<InstallationDetail> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/uninstall`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confirm_name: confirmName, reason_es: reasonEs }),
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos desinstalar el agente.");
  }
  // Deferred un-hire (centralized billing, 2026-06-16): the agent stays ACTIVE
  // with `scheduled_unhire_at` set — it is NOT archived immediately.
  return (await response.json()) as InstallationDetail;
}

// Deferred un-hire UNDO (centralized billing, 2026-06-16). `POST
// /installations/:id/keep` clears a pending `scheduled_unhire_at`, so the agent
// keeps running past the billing period it was about to come down on.
export async function keepAgent(
  installationId: string,
): Promise<InstallationDetail> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/keep`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    },
  );
  if (!response.ok) {
    throw await parseError(response, "No pudimos cancelar la baja del agente.");
  }
  return (await response.json()) as InstallationDetail;
}

// ---------------------------------------------------------------------------
// Centralized billing (2026-06-16). The appsnube-billing handshake.
// ---------------------------------------------------------------------------

// `GET /api/v1/billing/health` — subscription status for the global banner.
// FAIL-OPEN: on any error we return `unknown` so a billing outage never blocks
// the dashboard (the banner only ever shows for `past_due` / `suspended`).
export async function fetchBillingHealth(): Promise<BillingHealth> {
  try {
    const response = await authedFetch("/api/v1/billing/health");
    if (!response.ok) {
      return { status: "unknown", reason: "", grace_until: null, trial_ends_at: null };
    }
    return (await response.json()) as BillingHealth;
  } catch {
    return { status: "unknown", reason: "", grace_until: null, trial_ends_at: null };
  }
}

// `GET /api/v1/billing/embed-token?deep_link=…` — mint a single-use (≤90s)
// embedding token for the billing iframe. Sends a FRESH TN Nexo session token
// in the `X-Nexo-Session-Token` header (same handshake as refreshAppJwt) so the
// billing app can verify the merchant. The app-JWT still rides Authorization
// via authedFetch. A 503 (billing not configured) is surfaced as an ApiError so
// the caller can render the "todavía no está disponible" state.
export async function fetchEmbedToken(
  deepLink: string,
): Promise<BillingEmbedToken> {
  const nexoSessionToken = await getSessionToken(nexo);
  if (!nexoSessionToken) {
    throw new Error("Nexo no devolvió un session token");
  }
  const qs = new URLSearchParams({ deep_link: deepLink });
  const response = await authedFetch(
    `/api/v1/billing/embed-token?${qs.toString()}`,
    {
      headers: { "X-Nexo-Session-Token": nexoSessionToken },
    },
  );
  if (!response.ok) {
    throw await parseError(
      response,
      "No pudimos abrir Facturación. Probá de nuevo en un momento.",
    );
  }
  return (await response.json()) as BillingEmbedToken;
}

// ---------------------------------------------------------------------------
// Full-list worksheet checks — persistent "ya lo trabajé" marks on rows of the
// complete lists behind list-bearing insights (FullListModal). Keyed by
// (list_id, item_key) so they survive insight supersession across runs.
// ---------------------------------------------------------------------------

export async function fetchInsightListChecks(): Promise<InsightListChecksResponse> {
  const response = await authedFetch("/api/v1/insight-lists/checks");
  if (!response.ok) {
    throw await parseError(response, "No pudimos cargar tus marcas de la lista.");
  }
  return (await response.json()) as InsightListChecksResponse;
}

export async function toggleInsightListCheck(payload: {
  list_id: string;
  item_key: string;
  checked: boolean;
}): Promise<{ list_id: string; item_key: string; checked: boolean }> {
  const response = await authedFetch("/api/v1/insight-lists/checks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw await parseError(response, "No pudimos guardar la marca.");
  }
  return (await response.json()) as {
    list_id: string;
    item_key: string;
    checked: boolean;
  };
}

export async function fetchInstallationConfig(
  installationId: string,
): Promise<InstallationConfigResponse> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/configure`,
  );
  if (!response.ok) {
    throw await parseError(
      response,
      "No pudimos leer la configuración del agente.",
    );
  }
  return (await response.json()) as InstallationConfigResponse;
}

export async function saveInstallationConfig(
  installationId: string,
  payload: {
    connector_account_ids: Record<string, string>;
    autonomy_mode: string;
    approval_posture: "standard" | "conservative";
    auto_rollback_opt_out?: boolean;
    // L90 — cadence picker. Optional so callers that haven't wired up
    // the schedule section yet don't break.
    schedule?: import("./types").AgentSchedule | null;
    // Per-hire default explanation level (expert | novice). Optional — absent
    // leaves the existing value alone.
    communication_level?: "expert" | "novice";
  },
): Promise<InstallationConfigResponse> {
  const response = await authedFetch(
    `/api/v1/agents/installations/${encodeURIComponent(installationId)}/configure`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    throw await parseError(
      response,
      "No pudimos guardar la configuración del agente.",
    );
  }
  return (await response.json()) as InstallationConfigResponse;
}
